import React, { useEffect, useMemo } from "react";
import { Field, useField, useForm } from "react-final-form";
import { CloudUploadOutlined } from "@mui/icons-material";
import { Autocomplete, Box, CircularProgress, TextField as MuiTextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";

import { Table, TableBody, TableContainer, TableHead } from "ui";

import { BodyCell, HeaderCell, ModifiedTableRow } from "fond/export/ExportTable/ExportTable.styles";
import { required } from "fond/utils/validation";
import { NonIdealState } from "fond/widgets";

import { deriveSpliceConfigurationDefaults, parseCsvHeader } from "./helper";
import { FormData } from "./SetupForm";

interface SpliceViewConfigurationStepProps {
  isReadOnly?: boolean;
  hasExistingSpliceTables: boolean;
  isSpliceTablesLoading: boolean;
}

interface SpliceColumnFieldConfig {
  field: "ClosureColumn" | "CableInColumn" | "FibreInColumn" | "CableOutColumn" | "FibreOutColumn" | "TierInColumn" | "TierOutColumn" | "TypeColumn";
  label: string;
  required: boolean;
}

const SPLICE_COLUMN_FIELDS: SpliceColumnFieldConfig[] = [
  { field: "ClosureColumn", label: "Closure", required: true },
  { field: "CableInColumn", label: "Cable In", required: true },
  { field: "FibreInColumn", label: "Fibre In", required: true },
  { field: "TierInColumn", label: "Tier In", required: false },
  { field: "TypeColumn", label: "Type", required: false },
  { field: "CableOutColumn", label: "Cable Out", required: true },
  { field: "FibreOutColumn", label: "Fibre Out", required: true },
  { field: "TierOutColumn", label: "Tier Out", required: false },
];

const SpliceColumnField: React.FC<{ name: string; options: string[]; isRequired?: boolean; disabled?: boolean }> = ({
  name,
  options,
  isRequired,
  disabled,
}) => (
  <Field name={name} validate={isRequired ? required : undefined}>
    {({ input, meta }) => {
      const hasError = !!(meta.touched && meta.error);
      return (
        <>
          <BodyCell width={280}>
            <Autocomplete
              size="small"
              freeSolo
              disabled={disabled}
              options={options}
              value={input.value ?? ""}
              onInputChange={(_, newValue) => input.onChange(newValue || null)}
              onBlur={input.onBlur}
              renderInput={(params) => <MuiTextField {...params} placeholder="Column name" error={hasError} />}
            />
          </BodyCell>
          <BodyCell>
            {hasError && (
              <Typography variant="body2" color="error">
                {meta.error}
              </Typography>
            )}
          </BodyCell>
        </>
      );
    }}
  </Field>
);

const SpliceViewConfigurationStep: React.FC<SpliceViewConfigurationStepProps> = ({ isReadOnly, hasExistingSpliceTables, isSpliceTablesLoading }) => {
  const { change } = useForm();

  const {
    input: { value: layersConfigurationData },
  } = useField<FormData["CoreConfigInput"]["LayersConfiguration"]["Data"]>("CoreConfigInput.LayersConfiguration.Data");

  const {
    input: { value: spliceTables },
  } = useField<FormData["CoreConfigInput"]["SpliceTables"]>("CoreConfigInput.SpliceTables");

  const {
    meta: { dirty: isSpliceConfigurationDirty },
    input: { value: spliceConfiguration },
  } = useField<FormData["CoreConfigInput"]["SpliceConfiguration"]>("CoreConfigInput.SpliceConfiguration");

  const hasSpliceData = (spliceTables?.length ?? 0) > 0 || hasExistingSpliceTables;

  // Suggest the uploaded splice CSV's own column names
  const csvHeaderOptions = useMemo(() => {
    const firstTable = spliceTables?.[0];
    return firstTable?.Data ? parseCsvHeader(firstTable.Data) : [];
  }, [spliceTables]);

  // Pre-populate the column mapping from the uploaded layers_configuration.json (splice_elements.column_names) if
  // the user hasn't already set or edited it.
  useEffect(() => {
    if (!hasSpliceData || isSpliceConfigurationDirty || spliceConfiguration || !layersConfigurationData) return;

    change("CoreConfigInput.SpliceConfiguration", {
      ...deriveSpliceConfigurationDefaults(layersConfigurationData),
      TierInColumn: null,
      TierOutColumn: null,
      TypeColumn: null,
    });
  }, [change, hasSpliceData, isSpliceConfigurationDirty, layersConfigurationData, spliceConfiguration]);

  if (isSpliceTablesLoading) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}
        data-testid="splice-view-configuration-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasSpliceData) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <NonIdealState
          icon={<CloudUploadOutlined color="disabled" sx={{ fontSize: "3em" }} />}
          title="Splice data required"
          description="Upload splice table CSVs in the artifacts step to configure the splice view."
          data-testid="splice-view-configuration-non-ideal-state"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Splice View Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Map the column names used in your splice table CSVs to the fields FOND needs to build the Splice View.
      </Typography>

      <TableContainer sx={{ border: `1px solid ${grey[300]}`, borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <ModifiedTableRow>
              <HeaderCell>FOND field</HeaderCell>
              <HeaderCell width={280}>Mapped column</HeaderCell>
              <HeaderCell />
            </ModifiedTableRow>
          </TableHead>
          <TableBody>
            {SPLICE_COLUMN_FIELDS.map(({ field, label, required: isRequired }) => (
              <ModifiedTableRow key={field} sx={{ height: "42px" }}>
                <BodyCell>
                  <Typography variant="body2">
                    {label}
                    {isRequired && " *"}
                  </Typography>
                </BodyCell>
                <SpliceColumnField
                  name={`CoreConfigInput.SpliceConfiguration.${field}`}
                  options={csvHeaderOptions}
                  isRequired={isRequired}
                  disabled={isReadOnly}
                />
              </ModifiedTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SpliceViewConfigurationStep;
